import React from "react";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  TakeawayHeader,
  CustomerInfo,
  ProductSearch,
  ProductTable,
  OrderSummary,
} from "./components";
import { useTakeawayOrder } from "./hooks";
import { TOAST_CONFIG } from "./constants";

/**
 * Takeaway Orders Page
 * Main page for creating and managing takeaway orders
 */
const Takeaway = () => {
  const {
    loading,
    customer,
    setCustomer,
    products,
    gstPercent,
    subtotal,
    gstAmount,
    grandTotal,
    totalPackageCharge,
    handleAddProduct,
    updateQuantity,
    removeProduct,
    handleCancel,
    handleSubmit,
    isFormValid,
  } = useTakeawayOrder();

  return (
    <div className="h-screen flex flex-col bg-gradient-to-br from-gray-50 via-white to-gray-50 overflow-hidden">
      <ToastContainer {...TOAST_CONFIG} />

      {/* Header Section */}
      <div className="flex-shrink-0 px-3 pt-3">
        <TakeawayHeader />
      </div>

      {/* Main Content Area */}
      <div className="flex-1 overflow-y-auto lg:overflow-hidden flex flex-col lg:flex-row gap-4 p-3 lg:p-4 pb-20 lg:pb-4">
        {/* Left Column - Customer & Summary (Scrollable on mobile, sticky summary on desktop) */}
        <div className="w-full lg:w-96 flex-shrink-0 lg:overflow-visible space-y-4">
          <div className="space-y-4 lg:sticky lg:top-0">
            {/* Customer Card */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="bg-gray-900 px-4 py-2">
                <div className="flex items-center gap-2 text-white">
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                    />
                  </svg>
                  <h3 className="font-semibold text-sm">Customer Details</h3>
                </div>
              </div>
              <div className="p-3">
                <CustomerInfo customer={customer} setCustomer={setCustomer} />
              </div>
            </div>

            {/* Summary Card - Desktop Only */}
            <div className="hidden lg:block bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="bg-gradient-to-r from-green-500 to-emerald-600 px-4 py-2">
                <div className="flex items-center gap-2 text-white">
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                    />
                  </svg>
                  <h3 className="font-semibold text-sm">Order Summary</h3>
                </div>
              </div>
              <div className="p-3">
                <OrderSummary
                  subtotal={subtotal}
                  gstAmount={gstAmount}
                  gstPercent={gstPercent}
                  grandTotal={grandTotal}
                  packageCharge={totalPackageCharge}
                />
              </div>
              {/* Action Buttons */}
              <div className="border-t border-gray-200 p-3 bg-gray-50">
                <div className="flex gap-3">
                  <button
                    onClick={handleCancel}
                    className="flex-1 px-4 py-2.5 bg-white border-2 border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center gap-2 text-sm"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                    Cancel
                  </button>
                  <button
                    onClick={handleSubmit}
                    disabled={!isFormValid || loading}
                    className="flex-1 px-4 py-2.5 bg-gradient-to-r from-green-600 to-emerald-600 text-white font-medium rounded-lg hover:from-green-700 hover:to-emerald-700 disabled:from-gray-300 disabled:to-gray-400 disabled:cursor-not-allowed transition-all shadow-md hover:shadow-lg flex items-center justify-center gap-2 text-sm"
                  >
                    {loading ? (
                      <>
                        <svg
                          className="animate-spin h-4 w-4"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          />
                        </svg>
                        Processing...
                      </>
                    ) : (
                      <>
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                        Save & Accept
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - Products (Main scrollable area) */}
        <div className="flex-1 lg:min-h-0 lg:overflow-y-auto space-y-4">
          {/* Search Card */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="bg-gray-900 px-4 py-3">
              <div className="flex items-center gap-2 text-white">
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
                <h3 className="font-semibold text-sm">Search Products</h3>
              </div>
            </div>
            <div className="p-4">
              <ProductSearch onSelect={handleAddProduct} />
            </div>
          </div>

          {/* Products Card */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="bg-gray-900 px-4 py-3">
              <div className="flex items-center justify-between text-white">
                <div className="flex items-center gap-2">
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"
                    />
                  </svg>
                  <div>
                    <h3 className="font-semibold text-sm">Order Items</h3>
                    <p className="text-xs opacity-90">
                      {products.length} item{products.length !== 1 ? "s" : ""}
                    </p>
                  </div>
                </div>
                {/* Progress Indicator */}
                {products.length > 0 && (
                  <div className="flex items-center gap-2">
                    <div className="w-24 h-1.5 bg-white/30 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-white transition-all duration-300"
                        style={{
                          width: `${Math.min((products.length / 20) * 100, 100)}%`,
                        }}
                      />
                    </div>
                    <span className="text-xs font-medium opacity-90">
                      {products.length}/20
                    </span>
                  </div>
                )}
              </div>
            </div>
            <div className="p-4">
              <ProductTable
                products={products}
                updateQuantity={updateQuantity}
                removeProduct={removeProduct}
              />
            </div>
          </div>
        </div>

        {/* Order Summary - Mobile Only (before action buttons) */}
        <div className="lg:hidden px-3">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="bg-gradient-to-r from-green-500 to-emerald-600 px-4 py-3">
              <div className="flex items-center gap-2 text-white">
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                  />
                </svg>
                <h3 className="font-semibold text-sm">Order Summary</h3>
              </div>
            </div>
            <div className="p-4">
              <OrderSummary
                subtotal={subtotal}
                gstAmount={gstAmount}
                gstPercent={gstPercent}
                grandTotal={grandTotal}
                packageCharge={totalPackageCharge}
              />
            </div>
            {/* Action Buttons */}
            <div className="border-t border-gray-200 p-4 bg-gray-50">
              <div className="flex gap-3">
                <button
                  onClick={handleCancel}
                  className="flex-1 px-4 py-2.5 bg-white border-2 border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center gap-2 text-sm"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={!isFormValid || loading}
                  className="flex-1 px-4 py-2.5 bg-gradient-to-r from-green-600 to-emerald-600 text-white font-medium rounded-lg hover:from-green-700 hover:to-emerald-700 disabled:from-gray-300 disabled:to-gray-400 disabled:cursor-not-allowed transition-all shadow-md hover:shadow-lg flex items-center justify-center gap-2 text-sm"
                >
                  {loading ? (
                    <>
                      <svg
                        className="animate-spin h-4 w-4"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                      Processing...
                    </>
                  ) : (
                    <>
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      Save & Accept
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Takeaway;
